import { Injectable, inject } from '@angular/core';
import { SELECTORS } from '../utils/constants';
import { TemplateEditorService } from './template-editor.service';

@Injectable()
export class KeyboardService {
  private editorService = inject(TemplateEditorService);

  public handleUndoRedo(event: KeyboardEvent): boolean {
    const isMac = navigator.platform.toUpperCase().includes('MAC');
    const isUndo = (isMac ? event.metaKey : event.ctrlKey) && !event.shiftKey && event.key === 'z';
    const isRedo =
      (isMac ? event.metaKey && event.shiftKey : event.key === 'z') ||
      (event.ctrlKey && event.key === 'y');

    if (isUndo) {
      event.preventDefault();
      this.editorService.undo();
      return true;
    }

    if (isRedo) {
      event.preventDefault();
      this.editorService.redo();
      return true;
    }

    return false;
  }

  public handleDeleteSelected(
    event: KeyboardEvent,
    selectedElement: HTMLElement | null,
    isDropdownVisible: boolean,
    onDelete: (el: HTMLElement) => void,
  ): boolean {
    if (
      selectedElement &&
      !isDropdownVisible &&
      (event.key === 'Backspace' || event.key === 'Delete')
    ) {
      event.preventDefault();
      onDelete(selectedElement);
      return true;
    }

    return false;
  }

  public handleTextAndChipSelection(event: KeyboardEvent, editorElement: HTMLElement): void {
    const isMac = navigator.platform.toUpperCase().includes('MAC');
    const selection = window.getSelection();
    if (!selection) return;

    if ((isMac ? event.metaKey : event.ctrlKey) && event.key === 'a') {
      event.preventDefault();
      const range = document.createRange();
      range.selectNodeContents(editorElement);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }

    if (
      isMac &&
      event.metaKey &&
      event.shiftKey &&
      ['ArrowLeft', 'ArrowRight'].includes(event.key)
    ) {
      event.preventDefault();
      if (!selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      const newRange = document.createRange();

      if (event.key === 'ArrowLeft') {
        newRange.setStart(editorElement, 0);
        newRange.setEnd(range.endContainer, range.endOffset);
      } else {
        newRange.setStart(range.startContainer, range.startOffset);
        newRange.setEnd(editorElement, editorElement.childNodes.length);
      }

      selection.removeAllRanges();
      selection.addRange(newRange);
      return;
    }

    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;

    const isWordJump = isMac ? event.altKey : event.ctrlKey;

    if (isWordJump && event.shiftKey) {
      event.preventDefault();
      if (!selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      const chip = range.startContainer.parentElement?.closest(SELECTORS.CHIP_WRAPPER);

      if (chip) {
        const newRange = document.createRange();
        if (event.key === 'ArrowRight') {
          newRange.setStart(range.startContainer, range.startOffset);
          newRange.setEndAfter(chip);
        } else {
          newRange.setStartBefore(chip);
          newRange.setEnd(range.endContainer, range.endOffset);
        }

        selection.removeAllRanges();
        selection.addRange(newRange);
      }
    }
  }

  public handleSuggestionNavigation(
    event: KeyboardEvent,
    suggestions: any[],
    activeIndex: number,
    onIndexChange: (idx: number) => void,
    onSelect: (item: any) => void,
    onClose: () => void,
  ): void {
    if (suggestions.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        onIndexChange((activeIndex + 1) % suggestions.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        onIndexChange((activeIndex - 1 + suggestions.length) % suggestions.length);
        break;
      case 'Enter':
      case 'Tab':
        event.preventDefault();
        if (suggestions[activeIndex]) {
          onSelect(suggestions[activeIndex]);
        }

        break;
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
    }
  }
}
